/**
 * Schémas Zod du module Cuvées commerciales (Lot D) — partagés API ⇄ ERP.
 *
 * La CUVÉE COMMERCIALE est la gamme (« Brut Tradition ») : référentiel tenant
 * MUTABLE, sans versions — la succession de ses lots d'assemblage EST son
 * historique. PIÈGE de vocabulaire : ≠ fraction `cuvee` du pressurage (Lot C).
 * Source des énumérations/CHECKs : migration `0058_lot_d_cave.sql`
 * (`cuvees_commerciales`).
 */

import { z } from "zod";

import { categorieDosageSchema } from "./degorgement.js";
import { idSchema } from "./parcellaire.js";
import { stockParStadeSchema } from "./stock-bouteilles.js";

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroir des CHECK SQL)
// ────────────────────────────────────────────────────────────────────────────

/** Style de la gamme — miroir CHECK SQL `cuvees_commerciales.style`. */
export const cuveeStyleSchema = z.enum(["blanc", "blanc_de_blancs", "blanc_de_noirs", "rose"]);
export type CuveeStyle = z.infer<typeof cuveeStyleSchema>;

/**
 * Libellés d'affichage des styles de cuvée. SOURCE UNIQUE (DRY) réutilisée par
 * l'ERP et la boutique publique (fiche produit webshop). Un « blanc de blancs » /
 * « blanc de noirs » est la mention cépage/couleur reconnue de la Champagne — elle
 * porte l'information d'encépagement sur l'étiquette (pas de % par cépage requis).
 */
export const STYLE_CUVEE_LIBELLES: Readonly<Record<CuveeStyle, string>> = {
  blanc: "Blanc",
  blanc_de_blancs: "Blanc de blancs",
  blanc_de_noirs: "Blanc de noirs",
  rose: "Rosé",
};

/** Libellé lisible d'un style de cuvée (repli sur le code si valeur inconnue). */
export function libelleStyleCuvee(style: string): string {
  return STYLE_CUVEE_LIBELLES[style as CuveeStyle] ?? style;
}

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Nom de la cuvée (unique par domaine). */
export const cuveeNomSchema = z
  .string()
  .trim()
  .min(1, "Nom de cuvée requis.")
  .max(120, "Nom trop long (120 caractères max).");

/** Code stable de la gamme dans la référence interne des nouveaux tirages. */
export const cuveeCodeCourtSchema = z
  .string()
  .trim()
  .min(1, "Code court requis.")
  .max(10, "Code court trop long (10 caractères max).")
  .transform((valeur) => valeur.toUpperCase())
  .pipe(
    z
      .string()
      .regex(
        /^[A-Z][A-Z0-9]{0,9}$/,
        "Utilisez 1 à 10 lettres ou chiffres, en commençant par une lettre.",
      ),
  );

/** Texte libre long optionnel — vide normalisé en undefined. */
const optionalLongText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} trop long (${max} caractères max).`)
    .optional()
    .or(z.literal("").transform(() => undefined));

// ────────────────────────────────────────────────────────────────────────────
// Inputs — création / modification / suppression
// ────────────────────────────────────────────────────────────────────────────

const cuveePayloadShape = {
  /** Nom commercial de la gamme (unique par domaine). */
  nom: cuveeNomSchema,
  /** Code court stable, par exemple BT ou BB. */
  code_court: cuveeCodeCourtSchema,
  /** Style cible (blanc / blanc de blancs / blanc de noirs / rosé). */
  style: cuveeStyleSchema,
  /** Gamme habituellement millésimée (intention — la décision se prend à l'assemblage). */
  millesime_vise: z.boolean().default(false),
  /** Description courte (positionnement, style de vin). */
  description: optionalLongText(4000, "Description"),
  /** Notes internes libres. */
  notes: optionalLongText(4000, "Notes"),
} as const;

/** Input `cuvee.creer` — créer une gamme (2 clics + frappe nom, UX É1). */
export const cuveeCreerInput = z.object(cuveePayloadShape);
export type CuveeCreerInput = z.infer<typeof cuveeCreerInput>;

/** Input `cuvee.modifier` — patch d'une gamme (référentiel mutable). */
export const cuveeModifierInput = z
  .object({
    id: idSchema,
    ...cuveePayloadShape,
    /** Désactivation douce (gamme arrêtée, historique conservé). */
    actif: z.boolean(),
  })
  .partial({
    nom: true,
    code_court: true,
    style: true,
    millesime_vise: true,
    description: true,
    notes: true,
    actif: true,
  });
export type CuveeModifierInput = z.infer<typeof cuveeModifierInput>;

/** Input `cuvee.supprimer` — suppression dure (refusée si assemblages/reprises liés). */
export const cuveeSupprimerInput = z.object({ id: idSchema });
export type CuveeSupprimerInput = z.infer<typeof cuveeSupprimerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Inputs — lecture
// ────────────────────────────────────────────────────────────────────────────

/** Input `cuvee.getById` — fiche 360° d'une gamme. */
export const cuveeGetByIdInput = z.object({ id: idSchema });
export type CuveeGetByIdInput = z.infer<typeof cuveeGetByIdInput>;

/** Input `cuvee.lister` — filtres optionnels pour la liste tenant. */
export const cuveeListerInput = z
  .object({
    /** Défaut serveur : actives seulement. */
    actif: z.boolean().optional(),
    style: cuveeStyleSchema.optional(),
  })
  .optional();
export type CuveeListerInput = z.infer<typeof cuveeListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/** Représentation d'une cuvée commerciale renvoyée par l'API. */
export const cuveeSortieSchema = z.object({
  id: idSchema,
  nom: z.string(),
  /** Null sur une gamme historique qui n'a pas encore été qualifiée. */
  code_court: cuveeCodeCourtSchema.nullable(),
  style: cuveeStyleSchema,
  millesime_vise: z.boolean(),
  categorie_dosage_cible: categorieDosageSchema.nullable(),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  actif: z.boolean(),
  cree_le: z.date(),
});
export type CuveeSortie = z.infer<typeof cuveeSortieSchema>;

/** Une ligne de liste enrichie du mini-stock par stade (tuiles É1). */
export const cuveeListeLigneSchema = cuveeSortieSchema.extend({
  stock_par_stade: stockParStadeSchema,
});
export type CuveeListeLigne = z.infer<typeof cuveeListeLigneSchema>;

/** Sortie `cuvee.lister` — liste des gammes avec mini-stock. */
export const cuveeListeSortieSchema = z.array(cuveeListeLigneSchema);

/**
 * Sortie `cuvee.getById` — fiche 360° : gamme + stock ventilé par stade
 * (JAMAIS de total unique). Assemblages et chaîne des lots se lisent via leurs
 * modules (`assemblage.lister`, `stock.listerLots` filtrés par cuvée).
 */
export const cuveeDetailSortieSchema = cuveeSortieSchema.extend({
  stock_par_stade: stockParStadeSchema,
});
export type CuveeDetailSortie = z.infer<typeof cuveeDetailSortieSchema>;

/** Sortie `cuvee.creer` — id + date de création. */
export const cuveeCreerSortieSchema = z.object({
  id: idSchema,
  cree_le: z.date(),
});

/** Sortie `cuvee.modifier` — id + date de mise à jour. */
export const cuveeModifierSortieSchema = z.object({
  id: idSchema,
  updated_at: z.date(),
});

/** Sortie `cuvee.supprimer` — accusé de suppression. */
export const cuveeSupprimerSortieSchema = z.object({
  id: idSchema,
  supprime: z.literal(true),
});
